import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminLayout from './AdminLayout';

const API_BASE = process.env.REACT_APP_API_BASE || 'https://app.titanelectricreno.com:8080/api';

const SUB_JOB_COLORS = {
  'Vertical Drilling':   '#e87722',
  'Horizontal Drilling': '#2563eb',
  'Directional Drilling': '#2563eb',
  'Loops':               '#16a34a',
  'Shop/Trucking':       '#9333ea',
  'Power Company':       '#dc2626',
  'General':             '#6b7280',
};

function getToken() {
  try { return JSON.parse(localStorage.getItem('titan_session') || 'null')?.token; }
  catch { return null; }
}

function apiFetch(path, opts = {}) {
  const token = getToken();
  return fetch(`${API_BASE}${path}`, {
    ...opts,
    headers: { 'Content-Type': 'application/json', ...(token ? { 'x-worker-token': token } : {}), ...opts.headers },
  }).then(r => r.ok ? r.json() : r.json().then(e => Promise.reject(e)));
}

function ProgressBar({ complete, active, total }) {
  if (!total) return <div className="proj-progress-bar empty" />;
  const pctComplete = Math.round((complete / total) * 100);
  const pctActive   = Math.round((active   / total) * 100);
  return (
    <div className="proj-progress-bar">
      <div className="proj-progress-fill complete" style={{ width: `${pctComplete}%` }} />
      <div className="proj-progress-fill active"   style={{ width: `${pctActive}%` }} />
    </div>
  );
}

export default function AdminProjects() {
  const navigate = useNavigate();
  const [projects, setProjects]   = useState([]);
  const [loading, setLoading]     = useState(true);
  const [error, setError]         = useState(null);
  const [search, setSearch]       = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [promoting, setPromoting] = useState(null); // project id being moved

  useEffect(() => { loadProjects(); }, []);

  // Move a legacy (Trello) project into the live New Projects workflow by
  // attaching an awarded-bid shell, then open it so tabs can be added.
  async function promoteProject(p, e) {
    e.stopPropagation();
    if (!window.confirm(`Move "${p.name}" into New Projects?\n\nIt becomes a live job with the full workflow (add tabs like Submittals, Drilling, etc. after). Bid details stay blank — that's fine.`)) return;
    setPromoting(p.id);
    try {
      await apiFetch(`/projects/${p.id}/promote`, { method: 'POST' });
      navigate(`/admin/new-projects/${p.id}`);
    } catch (err) {
      alert(err?.error || err?.message || 'Could not move this project.');
      setPromoting(null);
    }
  }

  async function loadProjects() {
    setLoading(true);
    setError(null);
    try {
      const data = await apiFetch('/admin/projects/summary');
      setProjects(Array.isArray(data) ? data : []);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }

  const filtered = projects.filter(p =>
    !search || p.name.toLowerCase().includes(search.toLowerCase())
  );

  const totalTasks    = projects.reduce((s, p) => s + p.totalTasks, 0);
  const totalComplete = projects.reduce((s, p) =>
    s + Object.values(p.subJobs).reduce((ss, b) => ss + b.complete, 0), 0);

  return (
    <AdminLayout>
      <div className="admin-page-header">
        <div>
          <h1 className="admin-page-title">Projects</h1>
          <p className="admin-page-sub">
            {projects.length} active · {totalTasks} tasks · {totalTasks ? Math.round((totalComplete / totalTasks) * 100) : 0}% complete
          </p>
        </div>
        <div style={{ display: 'flex', gap: 10 }}>
          <button className="admin-primary-btn" onClick={() => setShowCreate(true)}>
            <svg width="13" height="13" viewBox="0 0 13 13" fill="none"><path d="M6.5 1v11M1 6.5h11" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/></svg>
            Create Project
          </button>
        </div>
      </div>

      {error && (
        <div className="mock-banner" style={{ borderColor: 'var(--red)', color: 'var(--red)' }}>
          {error}
        </div>
      )}

      <div className="admin-card">
        <div className="admin-card-header">
          <h2 className="admin-card-title">Active projects ({filtered.length})</h2>
          <div className="admin-search-wrap">
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none" style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: 'var(--gray-muted)' }}>
              <circle cx="6" cy="6" r="4.5" stroke="currentColor" strokeWidth="1.3" fill="none" />
              <path d="M9.5 9.5l3 3" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
            </svg>
            <input className="admin-search" placeholder="Search projects…" value={search} onChange={e => setSearch(e.target.value)} />
          </div>
        </div>

        {loading && <div className="admin-empty">Loading…</div>}
        {!loading && filtered.length === 0 && <div className="admin-empty">No projects found.</div>}

        {!loading && filtered.length > 0 && (
          <div style={{ overflowX: 'auto' }}>
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Project</th>
                  <th>Sub-jobs</th>
                  <th>Tasks</th>
                  <th>Progress</th>
                  <th>Last activity</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(p => {
                  const done    = Object.values(p.subJobs).reduce((s, b) => s + b.complete, 0);
                  const active  = Object.values(p.subJobs).reduce((s, b) => s + b.active, 0);
                  const pct     = p.totalTasks ? Math.round((done / p.totalTasks) * 100) : 0;
                  const subKeys = Object.keys(p.subJobs);
                  const lastAct = p.lastActivity
                    ? new Date(p.lastActivity).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
                    : '—';
                  return (
                    <tr
                      key={p.id}
                      style={{ cursor: 'pointer' }}
                      onClick={() => navigate(`/admin/projects/${p.id}`)}
                    >
                      <td style={{ fontWeight: 600, maxWidth: 260 }}>
                        <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {p.name}
                        </div>
                      </td>
                      <td>
                        <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                          {subKeys.map(k => (
                            <div
                              key={k}
                              title={k}
                              style={{ width: 10, height: 10, borderRadius: '50%', background: SUB_JOB_COLORS[k] || '#6b7280', flexShrink: 0 }}
                            />
                          ))}
                        </div>
                      </td>
                      <td style={{ color: 'var(--gray-muted)', fontSize: 13 }}>{p.totalTasks}</td>
                      <td style={{ minWidth: 120 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <div style={{ flex: 1 }}>
                            <ProgressBar complete={done} active={active} total={p.totalTasks} />
                          </div>
                          <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--orange)', width: 32 }}>{pct}%</span>
                        </div>
                      </td>
                      <td style={{ color: 'var(--gray-muted)', fontSize: 13, whiteSpace: 'nowrap' }}>{lastAct}</td>
                      <td onClick={e => e.stopPropagation()} style={{ whiteSpace: 'nowrap', textAlign: 'right' }}>
                        <button
                          onClick={e => promoteProject(p, e)}
                          disabled={promoting === p.id}
                          title="Move this project into New Projects (the live workflow)"
                          style={{ background: '#16a34a', color: '#fff', border: 'none', borderRadius: 7, padding: '6px 12px', fontSize: 12, fontWeight: 700, cursor: promoting === p.id ? 'default' : 'pointer', opacity: promoting === p.id ? 0.6 : 1 }}>
                          {promoting === p.id ? 'Moving…' : 'Move to New Projects →'}
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {showCreate && (
        <CreateProjectModal
          onClose={() => setShowCreate(false)}
          onCreated={id => { setShowCreate(false); navigate(`/admin/projects/${id}`); }}
        />
      )}
    </AdminLayout>
  );
}

// ── Default sub job list for new projects ──────────────────────────────────────
const DEFAULT_SUB_JOBS = ['Vertical Drilling','Shop','NVE','Conduit','Wire Pull','Terminations','Lighting','Panel','Gear','Inspection','Punch List'];

function CreateProjectModal({ onClose, onCreated }) {
  const [form, setForm] = useState({ name: '', clientName: '', projectNumber: '', description: '' });
  const [employees, setEmployees] = useState([]);
  const [memberIds, setMemberIds] = useState([]);
  const [subJobs, setSubJobs] = useState(DEFAULT_SUB_JOBS.map(n => ({ name: n, checked: false })));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    apiFetch('/users').then(data => setEmployees(Array.isArray(data) ? data : [])).catch(() => {});
  }, []);

  function f(key) { return { value: form[key], onChange: e => setForm(p => ({ ...p, [key]: e.target.value })) }; }
  function toggleMember(id) { setMemberIds(ids => ids.includes(id) ? ids.filter(x => x !== id) : [...ids, id]); }
  function toggleSubJob(name) { setSubJobs(sj => sj.map(s => s.name === name ? { ...s, checked: !s.checked } : s)); }

  async function save() {
    if (!form.name.trim()) { setError('Project name is required.'); return; }
    setSaving(true);
    try {
      const result = await apiFetch('/projects', {
        method: 'POST',
        body: JSON.stringify({
          ...form,
          memberIds,
          subJobNames: subJobs.filter(s => s.checked).map(s => s.name),
        }),
      });
      onCreated(result.id);
    } catch (e) {
      setError(e?.error || 'Failed to create project.');
      // The number belongs to another job: a pop-up, not only the red line (Ryan, Sep 30 2026).
      if (e?.code === 'number_taken') {
        // …and offer to open the job that already has it (WO26088, Oct 7 2026).
        if (e.existing && e.existing.id) { if (window.confirm(`${e.error}

Open ${e.existing.project_number || 'that job'} instead?`)) window.location.assign(`/admin/jobs/${e.existing.id}`); }
        else window.alert(e.error);
      }
    }
    setSaving(false);
  }

  return (
    <div className="admin-modal-overlay" onClick={onClose}>
      <div className="admin-modal" style={{ maxWidth: 680, maxHeight: '90vh', overflowY: 'auto' }} onClick={e => e.stopPropagation()}>
        <div className="admin-modal-header">
          <h3>Create Project</h3>
          <button className="admin-modal-close" onClick={onClose}>✕</button>
        </div>
        <div style={{ padding: '0 24px 24px', display: 'flex', flexDirection: 'column', gap: 20 }}>

          {/* Basic info */}
          <div>
            <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, color: 'var(--gray-muted)', marginBottom: 12 }}>Project Details</div>
            <div className="admin-form-row">
              <div className="field-group">
                <label className="field-label">Project Name *</label>
                <input className="field-input" placeholder="e.g. 2505 - COR Riverside Lighting" {...f('name')} />
              </div>
              <div className="field-group" style={{ maxWidth: 140 }}>
                <label className="field-label">Project #</label>
                <input className="field-input" placeholder="e.g. 2505" {...f('projectNumber')} />
              </div>
            </div>
            <div className="field-group" style={{ marginTop: 12 }}>
              <label className="field-label">Client Name</label>
              <input className="field-input" placeholder="Optional" {...f('clientName')} />
            </div>
            <div className="field-group" style={{ marginTop: 12 }}>
              <label className="field-label">Description</label>
              <textarea className="field-input" rows={2} style={{ resize: 'vertical' }} {...f('description')} />
            </div>
            {/* No dates here — the Schedule tiles own them (Ryan, Oct 1 2026). */}
          </div>

          {/* Sub jobs */}
          <div>
            <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, color: 'var(--gray-muted)', marginBottom: 12 }}>Sub Jobs</div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
              {subJobs.map(s => (
                <button
                  key={s.name}
                  type="button"
                  onClick={() => toggleSubJob(s.name)}
                  style={{ padding: '6px 14px', borderRadius: 20, border: `2px solid ${s.checked ? 'var(--orange)' : 'var(--border)'}`, background: s.checked ? 'var(--orange)' : '#fff', color: s.checked ? '#fff' : 'var(--text)', fontWeight: 600, fontSize: 13, cursor: 'pointer', transition: 'all 0.15s' }}
                >{s.name}</button>
              ))}
            </div>
          </div>

          {/* Workers */}
          <div>
            <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, color: 'var(--gray-muted)', marginBottom: 12 }}>
              Assign Workers ({memberIds.length} selected)
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px,1fr))', gap: 8, maxHeight: 220, overflowY: 'auto', padding: '2px 0' }}>
              {employees.map(e => {
                const sel = memberIds.includes(e.id);
                return (
                  <button
                    key={e.id}
                    type="button"
                    onClick={() => toggleMember(e.id)}
                    style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px', borderRadius: 8, border: `2px solid ${sel ? 'var(--orange)' : 'var(--border)'}`, background: sel ? '#fff7ed' : '#fff', cursor: 'pointer', textAlign: 'left', transition: 'all 0.12s' }}
                  >
                    <div style={{ width: 30, height: 30, borderRadius: '50%', background: sel ? 'var(--orange)' : '#e5e7eb', color: sel ? '#fff' : '#6b7280', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: 11, flexShrink: 0 }}>
                      {(e.firstName?.[0] || '') + (e.lastName?.[0] || '')}
                    </div>
                    <div>
                      <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)' }}>{e.firstName} {e.lastName}</div>
                      <div style={{ fontSize: 11, color: 'var(--gray-muted)' }}>{e.role}</div>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          {error && <div style={{ color: '#dc2626', fontSize: 13 }}>{error}</div>}

          <div style={{ display: 'flex', gap: 10 }}>
            <button onClick={onClose} style={{ flex: 1, padding: '11px 0', borderRadius: 8, border: '1.5px solid var(--border)', background: '#fff', color: 'var(--text)', fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>Cancel</button>
            <button onClick={save} disabled={saving} style={{ flex: 2, padding: '11px 0', borderRadius: 8, border: 'none', background: saving ? '#ccc' : 'var(--orange)', color: '#fff', fontSize: 14, fontWeight: 700, cursor: saving ? 'not-allowed' : 'pointer' }}>
              {saving ? 'Creating…' : 'Create Project'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
